import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SearchSelectField } from "@/components/SearchSelect";

/** Dialogue « Nouveau RDV » (réutilisable agenda + fiche patient). */
export function RdvDialog({ open, onClose, patients = [], careTypes = [], secteurs = [], prefillPatient = "", defaultType = "", onSaved }) {
  const [f, setF] = useState({ patient_id: "", secteur_id: "", date: "", duree_min: 60, type_soin: "", montant_du: 60, origine: "direct" });
  useEffect(() => {
    if (!open) return;
    const pt = patients.find((x) => x.id === (prefillPatient || ""));
    setF((prev) => ({ ...prev, patient_id: prefillPatient || "", type_soin: defaultType || "", secteur_id: (pt?.secteurs && pt.secteurs[0]) || "" }));
    /* eslint-disable-next-line */
  }, [open, prefillPatient, defaultType]);
  const onType = (ctId) => {
    const ct = careTypes.find((c) => c.id === ctId);
    setF((p) => ({ ...p, type_soin: ctId, ...(ct ? { duree_min: ct.duree || 60 } : {}) }));
  };
  const save = async () => {
    if (!f.date) { toast.error("Choisissez une date/heure"); return; }
    try {
      await api.post("/rdvs", {
        patient_id: f.patient_id || null, secteur_id: f.secteur_id || null,
        date: new Date(f.date).toISOString(), duree_min: Number(f.duree_min),
        type_rdv: "soin", type_soin: f.type_soin || "",
        montant_du: Number(f.montant_du), titre: "", origine: f.origine,
      });
      toast.success("RDV créé");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Nouveau rendez-vous</DialogTitle><DialogDescription>Planifiez un rendez-vous de soin pour un patient.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <SearchSelectField label="Patient" testid="rdv-patient" dialogTitle="Rechercher un patient"
            items={patients} value={f.patient_id} getLabel={(p) => `${p.prenom} ${p.nom}`} getSub={(p) => p.telephone || p.email}
            onChange={(id) => { const pt = patients.find((x) => x.id === id); setF((prev) => ({ ...prev, patient_id: id, secteur_id: (pt?.secteurs && pt.secteurs[0]) || prev.secteur_id })); }} placeholder="Sélectionner un patient" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Type de soin</label>
              <select data-testid="rdv-type" value={f.type_soin} onChange={(e) => onType(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300">
                <option value="">— Choisir —</option>
                {careTypes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
              {careTypes.length === 0 && <p className="text-[11px] text-slate-400 mt-1">Aucun type. Ajoutez-en dans Paramètres &gt; Types de soin.</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Secteur</label>
              <select data-testid="rdv-secteur" value={f.secteur_id} onChange={(e) => setF({ ...f, secteur_id: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300">
                <option value="">— Aucun —</option>
                {secteurs.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Date & heure</label>
              <input data-testid="rdv-date" type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Durée (min)</label>
              <input data-testid="rdv-duree" type="number" value={f.duree_min} onChange={(e) => setF({ ...f, duree_min: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
          <button data-testid="rdv-save" onClick={save} className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium">Créer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Dialogue « Nouveau soin » (fiche de soin sans RDV, ex. suivi téléphonique). */
export function SoinDialog({ open, onClose, patientId, careTypes = [], defaultType = "", onSaved }) {
  const [f, setF] = useState({ type_soin: "", date: "", duree_min: 60, lieu: "cabinet" });
  useEffect(() => { if (open) setF({ type_soin: defaultType || "", date: "", duree_min: 60, lieu: "cabinet" }); }, [open, defaultType]);
  const onType = (ctId) => {
    const ct = careTypes.find((c) => c.id === ctId);
    setF((p) => ({ ...p, type_soin: ctId, ...(ct ? { duree_min: ct.duree || 60 } : {}) }));
  };
  const save = async () => {
    try {
      const { data } = await api.post("/soins", {
        patient_id: patientId, date: f.date ? new Date(f.date).toISOString() : null,
        duree_min: Number(f.duree_min), lieu: f.lieu, type_soin: f.type_soin || "soin_cabinet",
      });
      toast.success("Fiche de soin créée");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Nouveau soin</DialogTitle><DialogDescription>Créez une fiche de soin sans rendez-vous (ex. suivi téléphonique).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Type de soin</label>
            <select data-testid="soin-type" value={f.type_soin} onChange={(e) => onType(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300">
              <option value="">— Choisir —</option>
              {careTypes.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-700">Date & heure</label>
              <input data-testid="soin-date" type="datetime-local" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300" /></div>
            <div><label className="text-sm font-medium text-slate-700">Lieu</label>
              <select data-testid="soin-lieu" value={f.lieu} onChange={(e) => setF({ ...f, lieu: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300">
                <option value="cabinet">Cabinet</option><option value="domicile">Domicile</option><option value="telephone">Téléphone</option>
              </select></div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm">Annuler</button>
          <button data-testid="soin-save" onClick={save} className="px-4 py-2 rounded-lg bg-[#141821] text-white text-sm font-medium">Créer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
